export const buildAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

export const safeJson = async (response) => {
  const text = await response.text();

  if (!text || text.trim() === "") {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const pickArray = (...candidates) =>
  candidates.find((candidate) => Array.isArray(candidate)) || [];

export const normalizeCategory = (category) => ({
  id: category?.id || category?.categoryId || category?.value || "",
  name: category?.name || category?.label || category?.title || "Unnamed",
  description: category?.description || "",
});

export const normalizeNicheCategoryState = (data) => {
  const seller =
    data?.seller || data?.sellerAccount || data?.account || data?.profile || {};
  const plan = data?.plan || seller?.plan || {};

  const selectedCategories = pickArray(
    data?.selectedCategories,
    seller?.selectedCategories,
  ).map(normalizeCategory);

  const availableCategories = pickArray(
    data?.availableCategories,
    data?.categories,
    seller?.availableCategories,
  ).map(normalizeCategory);

  const selectedCount =
    typeof data?.selectedCount === "number"
      ? data.selectedCount
      : selectedCategories.length;

  const maxCategories =
    Number(data?.maxCategories ?? plan?.maxCategories ?? 0) || 0;

  const maxProducts = Number(data?.maxProducts ?? plan?.maxProducts ?? 0) || 0;

  const remainingSelections =
    typeof data?.remainingSelections === "number"
      ? data.remainingSelections
      : Math.max(maxCategories - selectedCount, 0);

  return {
    sellerId: seller?.id || data?.sellerId || null,
    shopName: seller?.shopName || data?.shopName || "",
    isBanned: seller?.isBanned === true || data?.isBanned === true,
    selectedCategories,
    availableCategories,
    selectedCount,
    remainingSelections,
    maxCategories,
    maxProducts,
  };
};

export const normalizeProduct = (product) => {
  const category =
    product?.category || product?.nicheCategory || product?.selectedCategory || {};

  const productImages = pickArray(product?.productImages, product?.images)
    .map((image) => {
      if (typeof image === "string") return image;
      return image?.url || image?.imageUrl || image?.src || "";
    })
    .filter(Boolean);

  return {
    id: product?.id,
    name: product?.name || "",
    description: product?.description || "",
    quantity: Number(product?.quantity ?? product?.stock ?? 0) || 0,
    condition: String(product?.condition || "").toUpperCase(),
    isNegotiable: product?.isNegotiable === true,
    productImages,
    categoryId:
      product?.categoryId ||
      product?.nicheCategoryId ||
      category?.id ||
      product?.selectedCategoryId ||
      "",
    categoryName:
      category?.name || product?.categoryName || product?.category || "Unassigned",
    createdAt: product?.createdAt || "",
    updatedAt: product?.updatedAt || "",
  };
};

export const normalizeProductsResponse = (data) =>
  pickArray(data, data?.items, data?.products, data?.data).map(normalizeProduct);

export const buildProductsQuery = ({
  search,
  categoryId,
  condition,
  isNegotiable,
  sortBy,
}) => {
  const params = new URLSearchParams();

  if (search?.trim()) {
    params.set("search", search.trim());
  }

  if (categoryId) {
    params.set("filter.categoryId", categoryId);
  }

  if (condition?.trim()) {
    params.set("filter.condition", condition.trim().toUpperCase());
  }

  if (typeof isNegotiable === "boolean") {
    params.set("filter.isNegotiable", String(isNegotiable));
  }

  if (sortBy) {
    params.set("sortBy", sortBy);
  }

  return params.toString();
};

export const splitImageUrls = (value) =>
  value
    .split(/\r?\n|,/) 
    .map((item) => item.trim())
    .filter(Boolean);